import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { isEmpty } from 'lodash'
import {
  CRMTypes,
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  getAllMarketplaceTemplatesSalesforce,
  getAllMarketplaceTemplatesSalesforceInAllAccounts,
  getAllReferralTemplates,
  getCrmFieldsForTemplate,
  getHubspotFieldsForTemplate,
  getReferralTemplate,
  getReferralTemplateForSalesforce,
  postReferralTemplate,
  postReferralTemplateFromMappingService,
  putReferralTemplate,
  putReferralTemplateFromMappingService,
  resetReferralTemplate,
} from '../../api/mappingManager'
import {
  ReferralTemplateCreateFailureMessage,
  ReferralTemplateResetSuccess,
  ReferralTemplateSaveSuccess,
} from '../../common/utils/messagesContants'
import type { AppDispatch, RootState } from '../../store/types'
import type { CloudMarketplace } from '../productsListing/types'
import {
  MarketplaceMappingManagerActionTypes,
  type MarketplaceFieldMapping,
  type MarketplaceFieldMappingDimensions,
  type MarketplaceFieldMappingMetadata,
  type MarketplaceHubspotFieldsData,
  type MarketplaceSalesforceFieldsData,
  type MarketplaceTemplateDataResponse,
  type MarketplaceTemplatesDataFieldMappings,
  type MarketplaceTemplatesDetailsData,
  type ProductsWithDimensions,
} from './types'
import {
  camelizeKeyValueObject,
  camelizeSalesforceReferralTemplate,
  cloneMarketplaceTemplateAPI,
  deleteMarketplaceTemplateAPI,
  flattenSalesforceFields,
  getProductsWithDimensionsByCloud,
  mapSalesforceTemplatesToDetails,
  mapTemplateDataToDetails,
  snakeizeKeyValueObject,
  snakeizeMarketplaceReferralTemplate,
} from './utils'
import {
  readAzureHubspotTemplate,
  writeAzureHubspotTemplate,
} from './utils/azureHubspotTemplate'

export { MarketplaceMappingManagerActionTypes }
export { mapTemplateDataToDetails }
export { snakeizeMarketplaceReferralTemplate }
export { camelizeKeyValueObject }
export { snakeizeKeyValueObject }

export const setMarketplaceTemplatesInfo = (
  referralTemplateDataInfo: MarketplaceTemplatesDetailsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_MARKETPLACE_TEMPLATE_DATA_INFO,
  payload: referralTemplateDataInfo,
})

export const setMarketplaceReferralTemplate = (
  referralTemplateData: MarketplaceTemplateDataResponse
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA,
  payload: referralTemplateData,
})

export const setMarketplaceTemplateAsDraft = (isDraft: boolean) => ({
  type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT,
  payload: isDraft,
})

export const setAllHubspotFields = (
  fieldsData: MarketplaceHubspotFieldsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS,
  payload: fieldsData,
})

export const setAllSalesforceFields = (
  fieldsData: MarketplaceSalesforceFieldsData[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS,
  payload: fieldsData,
})

export const updateMarketplaceTemplateSectionField = (
  fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
  updates:
    | Partial<MarketplaceFieldMapping>
    | Partial<MarketplaceFieldMappingMetadata>
    | Partial<MarketplaceFieldMappingDimensions>
) => ({
  type: MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD,
  payload: { fieldKey, updates },
})

export const getMarketplaceReferralTemplatesInfo =
  (
    crmId: string,
    templateType?: string,
    crmtype?: string,
    accountId?: string,
    loading?: boolean,
    isActive?: boolean,
    cloudMarketplace?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const isAzureHubspot =
      crmtype === CRMTypes.hubspot &&
      cloudMarketplace?.toUpperCase() === 'AZURE'
    const effectiveTemplateType = isAzureHubspot
      ? 'marketplace'
      : (templateType ??
        (crmtype === CRMTypes.salesforce ? 'marketplace' : 'flyout'))
    if (!loading) {
      await dispatch(startLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
    try {
      if (isAzureHubspot && !accountId) {
        await dispatch(setMarketplaceTemplatesInfo([]))
        return
      }
      if (crmtype === CRMTypes.salesforce || isAzureHubspot) {
        let response: any = null
        if (accountId) {
          response = await getAllMarketplaceTemplatesSalesforce(
            crmId,
            accountId,
            effectiveTemplateType,
            isActive,
            isAzureHubspot ? 'AZURE' : undefined
          )
        } else {
          response = await getAllMarketplaceTemplatesSalesforceInAllAccounts(
            crmId,
            effectiveTemplateType,
            isActive
          )
        }
        if (!isEmpty(response?.data)) {
          const camelizedResponse = camelize(response.data) as any
          const templates = isAzureHubspot
            ? (camelizedResponse?.mappingTemplates || []).filter(
                (template: { cloudProvider?: string }) =>
                  template.cloudProvider?.toUpperCase() === 'AZURE'
              )
            : camelizedResponse?.mappingTemplates || []
          const usersData = getState()?.userList.user.userList.users || []

          const referralTemplatesData = mapSalesforceTemplatesToDetails(
            templates,
            usersData
          )
          await dispatch(setMarketplaceTemplatesInfo(referralTemplatesData))
        }
      } else {
        const response = await getAllReferralTemplates(
          crmId,
          isActive,
          effectiveTemplateType,
          crmtype
        )

        if (!isEmpty(response?.data)) {
          const templateDetailsData = camelize(response?.data?.data?.templates)
          const usersData = getState()?.userList.user.userList.users
          const referralTemplatesData = mapTemplateDataToDetails(
            templateDetailsData,
            usersData
          )
          await dispatch(setMarketplaceTemplatesInfo(referralTemplatesData))
        }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (!loading)
        await dispatch(stopLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
  }

export const createMarketplaceReferralTemplate =
  (templateData: Record<string, string>, crmId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
      const response = await postReferralTemplate(crmId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data?.data)
        const templateId = templateDataResponse?.id
        await dispatch(setMarketplaceReferralTemplate(templateDataResponse))
        return { templateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([
        ReferralTemplateCreateFailureMessage,
      ])(errorResponse)
      dispatch(
        updateAppAlert({
          message: getErrorMessages([ReferralTemplateCreateFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to create template. Please try again.',
    }
  }

export const createMarketplaceSalesforceTemplate =
  (
    templateData: Record<string, unknown>,
    crmId: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await postReferralTemplateFromMappingService(
              crmId,
              snakeize(templateData),
              accountId
            )
          : await postReferralTemplate(crmId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data)
        const templateId = templateDataResponse?.id
        return { templateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const apiMessage =
        (errorResponse as unknown as { data?: any })?.data?.message ||
        (errorResponse as unknown as { data?: any })?.data?.error ||
        (errorResponse as unknown as { data?: any })?.data?.detail ||
        (errorResponse as unknown as { data?: any })?.data?.errors?.[0]

      const errorMessage = apiMessage
        ? String(apiMessage)
        : getErrorMessages([RequestFailureMessage])(errorResponse)
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to create template. Please try again.',
    }
  }

export const cloneMarketplaceReferralTemplate =
  (
    templateData: Record<string, string>,
    crmId: string,
    templateId: string,
    accountId?: string,
    crmType?: CRMTypes,
    useMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
      const response = await cloneMarketplaceTemplateAPI({
        crmId,
        templateId,
        templateData,
        crmType,
        accountId,
        useMappingService,
      })
      if (!isEmpty(response?.data)) {
        const templateDataResponse =
          crmType === CRMTypes.salesforce || useMappingService
            ? camelize(response?.data)
            : camelize(response?.data?.data)
        const newTemplateId = templateDataResponse?.id
        await dispatch(setMarketplaceReferralTemplate(templateDataResponse))
        return { templateId: newTemplateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to clone template. Please try again.',
    }
  }

export const deleteMarketplaceReferralTemplate =
  (
    crmId: string,
    templateId: string,
    templateName: string,
    accountId?: string,
    crmType?: CRMTypes,
    useMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    try {
      await deleteMarketplaceTemplateAPI({
        crmId,
        templateId,
        accountId,
        crmType,
        useMappingService,
      })
      await dispatch(
        updateAppAlert({
          message: `${templateName} deleted successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([`${templateName} deletion failed.`])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    }
  }

export const resetMarketplaceReferralTemplate =
  (crmId: string, templateId: string, accountId?: string, crmType?: CRMTypes) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.marketplaceMappingManager?.templateData?.templateData
      const response =
        (crmType === CRMTypes.salesforce ||
          (crmType === CRMTypes.hubspot &&
            templateDataResponse.useTemplateFromMappingService)) &&
        accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              crmType === CRMTypes.hubspot
                ? writeAzureHubspotTemplate({
                    ...templateDataResponse,
                    resetToDefault: true,
                  })
                : snakeizeMarketplaceReferralTemplate({
                    ...templateDataResponse,
                    resetToDefault: true,
                  }),
              accountId ?? ''
            )
          : await resetReferralTemplate(
              crmId,
              templateId,
              snakeize(templateDataResponse)
            )
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateResetSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData =
          crmType === CRMTypes.hubspot &&
          templateDataResponse.useTemplateFromMappingService
            ? readAzureHubspotTemplate(response?.data)
            : camelize(
                crmType === CRMTypes.salesforce
                  ? response?.data
                  : response?.data?.data
              )
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    }
  }

export const updateMarketplaceReferralTemplateName =
  (
    crmId: string,
    templateId: string,
    templateData: Partial<MarketplaceTemplateDataResponse>,
    accountId?: string,
    useTemplateFromMappingService?: boolean,
    isSalesforce?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    try {
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeize(templateData),
              accountId
            )
          : await putReferralTemplate(crmId, templateId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = useMappingOn
          ? isSalesforce
            ? camelizeSalesforceReferralTemplate(response?.data)
            : readAzureHubspotTemplate(response?.data)
          : camelize(response?.data?.data)
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
        return true
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    }
  }

export const getMarketplaceReferralTemplate =
  (crmId: string, templateId: string, hasDiscared?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response = await getReferralTemplate(crmId, templateId)
      if (!isEmpty(response?.data)) {
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const getAllSalesforceFields =
  (crmId: string, accountId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_CRM_FIELDS))
    try {
      const response = await getCrmFieldsForTemplate(
        crmId,
        accountId,
        'Opportunity'
      )
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.fields)
        const updatedFields = flattenSalesforceFields(crmFieldData)
        await dispatch(
          setAllSalesforceFields(
            updatedFields as unknown as MarketplaceSalesforceFieldsData[]
          )
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_CRM_FIELDS))
    }
  }

export const getMarketplaceReferralTemplateSalesforce =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    hasDiscared?: boolean,
    crmType?: string,
    expectedTemplateType?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response = await getReferralTemplateForSalesforce(
        crmId,
        accountId,
        templateId
      )
      if (!isEmpty(response?.data)) {
        const currentReferralTemplateData =
          crmType === CRMTypes.hubspot
            ? readAzureHubspotTemplate(response?.data)
            : camelizeSalesforceReferralTemplate(response?.data)
        if (
          expectedTemplateType &&
          currentReferralTemplateData.templateType !== expectedTemplateType
        ) {
          dispatch(
            updateAppAlert({
              message: 'The selected template is not valid for this form.',
              messageType: 'ERROR',
              autoClose: true,
            })
          )
          return false
        }
        await dispatch(
          setMarketplaceReferralTemplate(
            currentReferralTemplateData as MarketplaceTemplateDataResponse
          )
        )
        return true
      }
      return false
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const saveMarketplaceReferralTemplate =
  (
    crmId: string,
    templateId: string,
    accountId?: string,
    useTemplateFromMappingService?: boolean,
    crmType?: string,
    cloudType?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.marketplaceMappingManager.templateData.templateData
      const currentUserId = getState()?.userProfile?.userProfile?.id
      templateDataResponse.updatedBy = currentUserId as string
      const shouldUseMappingService =
        crmType === CRMTypes.salesforce
          ? !!accountId
          : (useTemplateFromMappingService ?? false) && !!accountId
      const response =
        shouldUseMappingService && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              crmType === CRMTypes.hubspot &&
                templateDataResponse.useTemplateFromMappingService
                ? writeAzureHubspotTemplate(templateDataResponse)
                : snakeizeMarketplaceReferralTemplate(templateDataResponse),
              accountId
            )
          : await putReferralTemplate(
              crmId,
              templateId,
              snakeize(templateDataResponse)
            )

      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = shouldUseMappingService
          ? crmType === CRMTypes.hubspot
            ? readAzureHubspotTemplate(response.data)
            : camelizeSalesforceReferralTemplate(response.data)
          : camelize(response?.data?.data)
        await dispatch(
          setMarketplaceReferralTemplate(currentReferralTemplateData)
        )
        return true
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    }
  }

export const getAllMarketplaceHubspotFields =
  (crmId: string, objectType?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    try {
      const response = await getHubspotFieldsForTemplate(crmId, objectType)
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.data?.fields)
        await dispatch(setAllHubspotFields(crmFieldData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    }
  }

export const setProductsWithDimensions = (
  products: ProductsWithDimensions[]
) => ({
  type: MarketplaceMappingManagerActionTypes.SET_PRODUCTS_WITH_DIMENSIONS,
  payload: { products },
})

export const getMarketplaceProductsWithDimensions =
  (partnerId: string, cloudMarketplace: CloudMarketplace = 'AWS') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PRODUCTS_WITH_DIMENSIONS))
    try {
      const response = await getProductsWithDimensionsByCloud(
        partnerId,
        cloudMarketplace
      )
      const responseData = response?.data
      const productData = [
        responseData?.products,
        responseData?.data?.products,
        responseData?.data?.data?.products,
        responseData,
        responseData?.data,
        responseData?.data?.data,
      ].find(Array.isArray)
      const products = Array.isArray(productData)
        ? (camelize(productData) as ProductsWithDimensions[])
        : []
      await dispatch(setProductsWithDimensions(products))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PRODUCTS_WITH_DIMENSIONS))
    }
  }
